import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useReportTemplates,
  useCreateReportTemplate,
  useUpdateReportTemplate,
  useDeleteReportTemplate,
  useReportTemplateContent,
} from '../../../../api/hooks/useReportTemplates';
import { ReportBuilderState } from '../types/reportBuilder.types';

interface TemplateManagerProps {
  reportState: ReportBuilderState;
  onLoadTemplate: (templateJson: string) => void;
  currentTemplateId?: string | null;
  onTemplateSaved?: (templateId: string) => void;
}

const TemplateManager: React.FC<TemplateManagerProps> = ({
  reportState,
  onLoadTemplate,
  currentTemplateId,
  onTemplateSaved,
}) => {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadMenuAnchor, setLoadMenuAnchor] = useState<null | HTMLElement>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: templates = [], isLoading: isLoadingTemplates } = useReportTemplates();
  const createTemplate = useCreateReportTemplate();
  const updateTemplate = useUpdateReportTemplate();
  const deleteTemplate = useDeleteReportTemplate();
  const fetchContent = useReportTemplateContent();

  const handleSaveClick = () => {
    setTemplateName('');
    setTemplateDescription('');
    setError(null);
    setSaveDialogOpen(true);
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      setError('El nombre es requerido');
      return;
    }

    try {
      const templateJson = JSON.stringify(reportState);
      if (currentTemplateId) {
        await updateTemplate.mutateAsync({
          id: currentTemplateId,
          data: { name: templateName, description: templateDescription, templateJson },
        });
        onTemplateSaved?.(currentTemplateId);
      } else {
        const result = await createTemplate.mutateAsync({
          name: templateName,
          description: templateDescription || undefined,
          templateJson,
        });
        onTemplateSaved?.(result.id);
      }
      setSaveDialogOpen(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al guardar la plantilla';
      setError(message);
    }
  };

  const handleLoadClick = (event: React.MouseEvent<HTMLElement>) => {
    setLoadMenuAnchor(event.currentTarget);
  };

  const handleLoad = async (templateId: string) => {
    try {
      const templateJson = await fetchContent.mutateAsync(templateId);
      onLoadTemplate(templateJson);
      setLoadMenuAnchor(null);
    } catch {
      alert('Error al cargar la plantilla.');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTemplate.mutateAsync(id);
    } catch {
      // Error is handled by the mutation state
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Tooltip title="Guardar plantilla">
        <IconButton onClick={handleSaveClick} color="primary" size="small">
          <SaveIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Cargar plantilla">
        <IconButton onClick={handleLoadClick} color="primary" size="small">
          <FolderOpenIcon />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={loadMenuAnchor}
        open={Boolean(loadMenuAnchor)}
        onClose={() => setLoadMenuAnchor(null)}
        PaperProps={{ sx: { maxHeight: 400, width: 300 } }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
          Plantillas guardadas
        </Typography>
        <Divider />
        {isLoadingTemplates ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : templates.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No hay plantillas guardadas
            </Typography>
          </MenuItem>
        ) : (
          templates.map((template) => (
            <MenuItem
              key={template.id}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              onClick={() => handleLoad(template.id)}
              disabled={fetchContent.isPending}
            >
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography variant="body2" noWrap>
                  {template.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  v{template.version}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(template.id);
                }}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        )}
      </Menu>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Guardar plantilla</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth
            label="Nombre"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            sx={{ mt: 1 }}
            required
          />
          <TextField
            fullWidth
            label="Descripción"
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={createTemplate.isPending || updateTemplate.isPending}
          >
            {(createTemplate.isPending || updateTemplate.isPending) ? (
              <CircularProgress size={20} />
            ) : (
              'Guardar'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateManager;
